import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Drawer, ConfirmationModal, Icon, notification } from '../common';
import FeedbackAttachment from './FeedbackAttachment';
import { useSubmitFeedbackMutation } from '../../apis/feedback';
import { useLazyGetS3PreSignedUrlQuery } from '../../apis/common';
import { uploadFeedbackScreenshot } from '../../helpers/fileHelpers/feedbackScreenshotHelper';
import { feedbackSubmitEvent } from '../../services/analyticsService';

const CATEGORY_KEYS = ['feedback', 'feature_request', 'issue'];
const CATEGORY_LABELS = { feedback: 'Feedback', feature_request: 'Feature request', issue: 'Issue' };

// Shell + form + submit flow + rate-limited state + discard-confirm for the feedback drawer.
// Stays mounted (just visually closed) even while FeedbackTab hides the tab element itself, so
// the shared Drawer's own AntD mask is what useBlockingOverlayState picks up.
const FeedbackDrawer = forwardRef(({ user, locale, pageSection, config, onCloseDrawer }, ref) => {
  const { t } = useTranslation();
  const { isMobile, rtl } = useSelector((state) => state.app.AppConfig);
  const drawerRef = useRef();
  const confirmRef = useRef();
  // Bumped on every pick / remove / reset so a slower, superseded upload can't overwrite the
  // state of a newer attachment (or of a cleared one).
  const uploadTokenRef = useRef(0);

  const [category, setCategory] = useState('feedback');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [imageId, setImageId] = useState(null); // presigned uuid, resolved at pick time (not submit)
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null); // { type: 'error' | 'rate_limited', message: string | null } | null

  const [submitFeedback] = useSubmitFeedbackMutation();
  const [getS3PreSignedUrl] = useLazyGetS3PreSignedUrlQuery();

  useImperativeHandle(ref, () => ({
    openDrawer: () => drawerRef.current?.openDrawer(),
    closeDrawer: () => drawerRef.current?.closeDrawer(),
  }));

  const resetState = () => {
    uploadTokenRef.current += 1; // cancel any in-flight upload so its result is ignored
    setCategory('feedback');
    setMessage('');
    setAttachment(null);
    setImageId(null);
    setUploadStatus('idle');
    setSubmitError(null);
    setSubmitting(false);
  };

  // Kicks off the presign + S3 PUT for `file`, tagging the run with `token`. Only the newest run
  // (uploadTokenRef.current) is allowed to write state back, so a removed/replaced attachment
  // whose upload is still in flight never resurfaces.
  const uploadAttachment = async (file, token) => {
    setUploadStatus('uploading');
    try {
      const uploadedId = await uploadFeedbackScreenshot(file, getS3PreSignedUrl);
      if (token !== uploadTokenRef.current) return;
      setImageId(uploadedId);
      setUploadStatus('success');
    } catch {
      if (token !== uploadTokenRef.current) return;
      setImageId(null);
      setUploadStatus('error');
    }
  };

  // Upload starts as soon as the user picks an image (T10) rather than on submit, so Send only
  // has to reference the already-resolved image_id.
  const handleAttachmentChange = (file) => {
    uploadTokenRef.current += 1;
    const token = uploadTokenRef.current;
    setImageId(null);
    setAttachment(file);
    if (!file) {
      setUploadStatus('idle');
      return;
    }
    uploadAttachment(file, token);
  };

  const handleRetryUpload = () => {
    if (!attachment) return;
    uploadTokenRef.current += 1;
    uploadAttachment(attachment, uploadTokenRef.current);
  };

  const messageMin = config?.message_min ?? 10;
  const messageMax = config?.message_max ?? 2000;
  const imageMaxMb = config?.image_max_mb ?? 5;
  // null when the feature is off or signed out — never treated as rate-limited (T11); the enabled
  // gate in FeedbackTab already covers the off case.
  const isRateLimited = config?.remaining_today === 0;
  const formDisabled = submitting || isRateLimited;

  const trimmedLength = message.trim().length;
  // An attached image must finish uploading before we can submit — its image_id is only known
  // once the upload resolves; a still-uploading or failed upload blocks Send.
  const attachmentPending = !!attachment && uploadStatus !== 'success';
  // The `maxLength` attribute on the textarea blocks typing past the cap, but isn't a reliable
  // guarantee across every input path (e.g. some browsers' paste handling), so canSend enforces
  // the upper bound itself rather than trusting the DOM alone.
  const canSend =
    trimmedLength >= messageMin && message.length <= messageMax && !formDisabled && !attachmentPending;

  // Intercepts every close trigger (mask click, Escape, our own X / Cancel) via the Drawer's
  // onClose override — see src/components/common/drawer/drawer.js: passing `onClose` through
  // props overrides the internal handler for those triggers while leaving the imperative
  // closeDrawer() ref method (used below once a close is actually confirmed) untouched.
  const requestClose = () => {
    if (submitting) return;
    if (message.trim().length > 0) {
      confirmRef.current?.showModal();
    } else {
      drawerRef.current?.closeDrawer();
    }
  };

  const handleDiscard = () => {
    confirmRef.current?.hideModal();
    drawerRef.current?.closeDrawer();
  };

  const handleKeepWriting = () => {
    confirmRef.current?.hideModal();
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // The screenshot was already uploaded at pick time; only its resolved image_id is sent here.
      const result = await submitFeedback({ category, message: message.trim(), image_id: imageId }).unwrap();
      feedbackSubmitEvent(user, {
        status: 'success',
        feedbackType: category,
        hasAttachment: !!attachment,
        characterCount: trimmedLength,
        pageSection,
        language: locale,
      });
      // The success toast is the backend's own copy (e.g. "Thanks, we got it") — never our
      // hardcoded translation — so it stays correct if the backend's wording changes.
      notification.success(result?.message || t('Thanks, we got your feedback'));
      drawerRef.current?.closeDrawer();
    } catch (e) {
      const isRateLimitedResponse = e?.status === 429;
      const backendMessage = typeof e?.message === 'string' ? e.message : null;
      feedbackSubmitEvent(user, {
        status: 'error',
        value: backendMessage,
        feedbackType: category,
        hasAttachment: !!attachment,
        characterCount: trimmedLength,
        pageSection,
        language: locale,
      });
      setSubmitting(false);
      // Same rule as success: show the backend's own error/rate-limit copy when it sent one;
      // our hardcoded copy is only a fallback for failures with no response body (network error).
      setSubmitError({ type: isRateLimitedResponse ? 'rate_limited' : 'error', message: backendMessage });
    }
  };

  const disclosureText = pageSection
    ? t('We attach the page you are on ({{pageName}}) and your account details.', { pageName: pageSection })
    : t('We attach your account details.');

  return (
    <>
      <Drawer
        ref={drawerRef}
        placement={rtl ? 'left' : 'right'}
        width={isMobile ? '100%' : 400}
        maskClosable={!submitting}
        keyboard={!submitting}
        onClose={requestClose}
        onCloseDrawer={() => {
          resetState();
          onCloseDrawer?.();
        }}
        headerStyle={{ padding: '18px 20px', borderBottom: '1px solid #EDEDED', alignItems: 'flex-start' }}
        bodyStyle={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto' }}
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{t('Send us feedback')}</div>
            <div style={{ fontSize: 13, color: '#707070', marginTop: 2 }}>
              {t('The product team reads every message.')}
            </div>
          </div>
        }
        extra={
          <span
            role="button"
            tabIndex={submitting ? -1 : 0}
            aria-label={t('Close')}
            onClick={() => !submitting && requestClose()}
            onKeyDown={(e) => {
              if (!submitting && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                requestClose();
              }
            }}
            style={{
              cursor: submitting ? 'default' : 'pointer',
              color: '#707070',
              display: 'flex',
              paddingTop: 2,
              opacity: submitting ? 0.5 : 1,
            }}
          >
            <Icon icon="IoMdClose" />
          </span>
        }
        footer={
          isRateLimited ? (
            <div style={{ fontSize: 13, color: '#707070', textAlign: 'center' }}>
              {t("You've sent the maximum number of messages for today. Please try again tomorrow.")}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={requestClose}
                disabled={submitting}
                style={{
                  flex: '0 0 auto',
                  padding: '11px 18px',
                  borderRadius: 8,
                  border: '1px solid #E0E0E0',
                  background: '#fff',
                  color: '#222',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: submitting ? 'default' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                style={{
                  flex: 1,
                  padding: '11px 18px',
                  borderRadius: 8,
                  border: '1px solid #B3D0D2',
                  background: canSend ? '#F5FBFB' : '#F5F5F5',
                  color: canSend ? '#006169' : '#B0B0B0',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: canSend ? 'pointer' : 'default',
                }}
              >
                {submitting ? t('Sending…') : t('Send feedback')}
              </button>
            </div>
          )
        }
      >
        {submitError && (
          <div
            style={{
              background: '#FFF1F0',
              border: '1px solid #FFCCC7',
              color: '#CF1322',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13,
            }}
          >
            {submitError.message ||
              (submitError.type === 'rate_limited'
                ? t("You've sent the maximum number of messages for today. Please try again tomorrow.")
                : t("We couldn't send your feedback. Your message is saved here — please try again."))}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{t('What kind of feedback is this?')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {CATEGORY_KEYS.map((key) => {
              const selected = key === category;
              return (
                <div
                  key={key}
                  role="button"
                  aria-pressed={selected}
                  tabIndex={formDisabled ? -1 : 0}
                  onClick={() => !formDisabled && setCategory(key)}
                  onKeyDown={(e) => {
                    if (!formDisabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setCategory(key);
                    }
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: formDisabled ? 'default' : 'pointer',
                    border: `1px solid ${selected ? '#006169' : '#E0E0E0'}`,
                    background: selected ? '#E1F2F0' : '#fff',
                    color: selected ? '#006169' : '#555',
                    opacity: formDisabled ? 0.6 : 1,
                  }}
                >
                  {t(CATEGORY_LABELS[key])}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t('Tell us more')}</div>
          <textarea
            value={message}
            maxLength={messageMax}
            disabled={formDisabled}
            onChange={(e) => setMessage(e.target.value.slice(0, messageMax))}
            placeholder={t('Describe what happened, or the change you would like to see.')}
            style={{
              minHeight: 140,
              resize: 'vertical',
              border: '1px solid #E0E0E0',
              borderRadius: 10,
              padding: 12,
              fontSize: 14,
              lineHeight: 1.5,
              color: '#222',
              fontFamily: 'inherit',
            }}
          />
          {message.length > 0 && trimmedLength < messageMin && (
            <div style={{ fontSize: 12, color: '#CF1322' }}>
              {t('Please enter at least {{min}} characters.', { min: messageMin })}
            </div>
          )}
          {message.length >= messageMax && (
            <div style={{ fontSize: 12, color: '#707070' }}>
              {t("You've reached the {{max}} character limit.", { max: messageMax })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <FeedbackAttachment
            file={attachment}
            onChange={handleAttachmentChange}
            onRetry={handleRetryUpload}
            uploadStatus={uploadStatus}
            imageMaxMb={imageMaxMb}
            disabled={formDisabled}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#707070' }}>
            <Icon icon="FeedbackInfoIcon" size={15} />
            {disclosureText}
          </div>
        </div>
      </Drawer>

      <ConfirmationModal
        ref={confirmRef}
        title="Discard your feedback?"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              type="button"
              onClick={handleKeepWriting}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #E0E0E0',
                background: '#fff',
                color: '#222',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Keep writing')}
            </button>
            <button
              type="button"
              onClick={handleDiscard}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: '#006169',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Discard')}
            </button>
          </div>
        }
      >
        <div style={{ fontSize: 14, color: '#222' }}>{t('Are you sure you want to discard changes?')}</div>
      </ConfirmationModal>
    </>
  );
});

export default FeedbackDrawer;
